/* oxlint-disable tailwindcss/no-unknown-classes -- Styles are colocated in push-to-searchable.css. */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ChangeEvent,
  type ReactNode,
} from "react"

import { useElementWidth } from "@/hooks/useElementWidth"
import { useIsVisible } from "@/hooks/useIsVisible"
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"

import {
  embedCaptions,
  embedDuration,
  failureCaptions,
  failureDuration,
  type Caption,
} from "./push-to-searchable-data"

import "./push-to-searchable.css"

type SearchPath = "embed" | "fail"
type PushToSearchableColor = "orange" | "indigo"

interface SceneValues {
  backoff: number[]
  cameraTransform: string
  caption: Caption
  checkpointOpacity: number
  chunkOpacity: number
  chunkText: string[]
  deadLetterOpacity: number
  embedFailureOpacity: number
  embedOpacity: number
  indexerOpacity: number
  progress: number[]
  repoOpacity: number
  retryOpacity: number
  searchHeight: number
  stackOpacity: number
  stackWireProgress: number
  stripHighlight: number
  stripOpacity: number
  stripRowsOpacity: number[]
  transcriptOpacity: number
  vectorText: string[]
  wireProgress: number[]
}

const animationSpeed = 0.49
const sceneWidth = 1920
const sceneHeight = 788

const focusAreas = {
  push: [50, 234, 220, 178],
  indexer: [236, 20, 340, 432],
  strip: [520, 150, 366, 396],
  chunk: [862, 216, 356, 276],
  embed: [1194, 216, 356, 276],
  retry: [862, 430, 682, 320],
  land: [1580, 98, 280, 456],
} as const

const stripRows = [
  "prompt: fix the flaky test",
  "tool_call: bash",
  "tool_result: 42 passed",
  "thinking…",
  "prose: race was in setup",
]

function clamp(value: number) {
  return Math.min(1, Math.max(0, value))
}

function easeOut(value: number) {
  return 1 - Math.pow(1 - clamp(value), 3)
}

function progressBetween(time: number, start: number, end: number) {
  return easeOut((time - start) / (end - start))
}

function linearProgress(time: number, start: number, end: number) {
  return clamp((time - start) / (end - start))
}

function getDuration(path: SearchPath) {
  return path === "fail" ? failureDuration : embedDuration
}

function getCaptions(path: SearchPath) {
  return path === "fail" ? failureCaptions : embedCaptions
}

function getCurrentCaption(path: SearchPath, time: number) {
  const captions = getCaptions(path)
  return captions.find((caption) => time < caption.end) ?? captions[captions.length - 1]!
}

function getCameraTransform(width: number, caption: Caption) {
  const isNarrow = width < 760
  const scale = Math.max(width / sceneWidth, isNarrow ? 0.44 : 0)
  const area = caption.focus ? focusAreas[caption.focus] : null

  if (!area) {
    const centeredX = (width - sceneWidth * scale) / 2
    return `translate(${centeredX}px, 0) scale(${scale})`
  }

  const [left, top, areaWidth, areaHeight] = area
  const visibleWidth = Math.max(1, width) / scale
  const padding = isNarrow ? 60 : 70
  const zoom = Math.min(
    isNarrow ? 2.8 : 2,
    Math.max(
      isNarrow ? 1.2 : 1,
      Math.min(visibleWidth / (areaWidth + padding * 2), sceneHeight / (areaHeight + padding * 2)),
    ),
  )
  const centerX = left + areaWidth / 2
  const centerY = top + areaHeight / 2
  const translateX = width / 2 - centerX * scale * zoom
  const translateY = (sceneHeight * scale) / 2 - centerY * scale * zoom

  return `translate(${translateX}px, ${translateY}px) scale(${scale * zoom})`
}

function getSceneValues(path: SearchPath, time: number, width: number): SceneValues {
  const isFailure = path === "fail"
  const captions = getCaptions(path)
  const caption = getCurrentCaption(path, time)
  const stripHighlight = progressBetween(time, 5.5, 6.1)
  const stripFade = progressBetween(time, 6.2, 6.8)
  const vectorStart = isFailure ? 13.4 : 8.6
  const landStart = isFailure ? 14.7 : 9.7
  const transcriptStart = isFailure ? 16.1 : 11.3
  const transcriptOpacity = progressBetween(time, transcriptStart, transcriptStart + 0.45)

  return {
    backoff: [
      isFailure ? progressBetween(time, 10.6, 11) : 0,
      isFailure ? progressBetween(time, 11.2, 11.6) : 0,
      isFailure ? progressBetween(time, 11.9, 12.3) : 0,
    ],
    cameraTransform: getCameraTransform(width, caption),
    caption,
    checkpointOpacity: progressBetween(time, 2.9, 3.4),
    chunkOpacity: progressBetween(time, 6.9, 7.4),
    chunkText: [0, 1].map((index) => {
      const text = index === 0 ? "chunk 01 · 700 tokens" : "chunk 02 · 412 tokens"
      const typed = linearProgress(time, 7.2 + index * 0.4, 8 + index * 0.4)
      const value = text.slice(0, Math.round(text.length * typed))
      return typed > 0 && typed < 1 ? `${value}█` : value
    }),
    deadLetterOpacity: isFailure ? progressBetween(time, 10.5, 11.1) * 0.9 : 0,
    embedFailureOpacity: isFailure
      ? progressBetween(time, 8.4, 9) * (1 - progressBetween(time, 12.6, 13.1))
      : 0,
    embedOpacity: progressBetween(time, 8, 8.5),
    indexerOpacity: progressBetween(time, 1.5, 2.1),
    progress: captions.map((item, index) => {
      const start = index === 0 ? 0 : captions[index - 1]!.end
      return linearProgress(time, start, item.end)
    }),
    repoOpacity: progressBetween(time, 0, 0.6),
    retryOpacity: isFailure ? progressBetween(time, 10, 10.6) : 0,
    searchHeight: 157 + 71 * progressBetween(time, landStart - 0.9, landStart + 1.1),
    stackOpacity: progressBetween(time, 4.3, 5),
    stackWireProgress: linearProgress(time, 4.3, 5.6),
    stripHighlight: stripHighlight * (1 - stripFade),
    stripOpacity: progressBetween(time, 3.7, 4.2),
    stripRowsOpacity: stripRows.map((_, index) =>
      progressBetween(time, 4 + index * 0.16, 4.5 + index * 0.16),
    ),
    transcriptOpacity,
    vectorText: [0, 1].map((index) => {
      const text = `[0.${12 + index * 7} −0.4${index + 1} 0.9${index} …]`
      const typed = linearProgress(
        time,
        vectorStart + index * 0.35,
        vectorStart + 0.9 + index * 0.35,
      )
      const value = text.slice(0, Math.round(text.length * typed))
      return typed > 0 && typed < 1 ? `${value}█` : value
    }),
    wireProgress: [
      progressBetween(time, 0.6, 1),
      progressBetween(time, 2.1, 2.6),
      progressBetween(time, 3.5, 4.2),
      progressBetween(time, 6.9, 7.4),
      progressBetween(time, 8.4, 8.9),
      linearProgress(time, landStart, landStart + 0.35),
    ],
  }
}

interface DiagramPanelProps {
  accent?: boolean
  children: ReactNode
  className: string
  label: string
  meta?: string
  style?: CSSProperties
}

function DiagramPanel({
  accent = false,
  children,
  className,
  label,
  meta,
  style,
}: DiagramPanelProps) {
  return (
    <div
      className={`push-search-panel ${accent ? "push-search-panel--accent" : ""} ${className}`}
      style={style}
    >
      <div className="push-search-panel__header">
        <span>{label}</span>
        {meta ? <span className="push-search-panel__meta">{meta}</span> : null}
      </div>
      <div className="push-search-panel__body">{children}</div>
    </div>
  )
}

function PipelineScene({ values }: { values: SceneValues }) {
  return (
    <div className="push-search-scene" style={{ transform: values.cameraTransform }}>
      <svg
        className="push-search-wires"
        viewBox={`0 0 ${sceneWidth} ${sceneHeight}`}
        aria-hidden="true"
      >
        <path
          className="push-search-wire"
          d="M260 352 H315"
          pathLength="1"
          style={{ strokeDashoffset: 1 - (values.wireProgress[0] ?? 0) }}
        />
        <path
          className="push-search-wire"
          d="M336 352 H372"
          pathLength="1"
          style={{ strokeDashoffset: 1 - (values.wireProgress[1] ?? 0) }}
        />
        <path
          className="push-search-wire"
          d="M315 352 V56 H1550 V276 H1568"
          pathLength="1"
          style={{ strokeDashoffset: 1 - values.stackWireProgress }}
        />
        <path
          className="push-search-wire push-search-wire--accent"
          d="M514 352 H546"
          pathLength="1"
          style={{ strokeDashoffset: 1 - (values.wireProgress[2] ?? 0) }}
        />
        <path
          className="push-search-wire push-search-wire--accent"
          d="M856 352 H888"
          pathLength="1"
          style={{ strokeDashoffset: 1 - (values.wireProgress[3] ?? 0) }}
        />
        <path
          className="push-search-wire push-search-wire--accent"
          d="M1188 352 H1220"
          pathLength="1"
          style={{ strokeDashoffset: 1 - (values.wireProgress[4] ?? 0) }}
        />
        <path
          className="push-search-wire push-search-wire--accent"
          d="M1520 352 H1576"
          pathLength="1"
          style={{ strokeDashoffset: 1 - (values.wireProgress[5] ?? 0) }}
        />
        <path
          className="push-search-wire push-search-wire--failure"
          d="M1370 464 V600"
          pathLength="1"
          style={{ strokeDashoffset: 1 - values.retryOpacity }}
        />
      </svg>

      <DiagramPanel
        className="push-search-repo"
        label="your repo"
        style={{
          opacity: values.repoOpacity,
          transform: `translateY(${(1 - values.repoOpacity) * 14}px)`,
        }}
      >
        <div className="push-search-row">$ git push</div>
      </DiagramPanel>

      <div className="push-search-indexer" style={{ opacity: values.indexerOpacity }}>
        <div className="push-search-indexer__diamond" />
        <span>indexer</span>
      </div>

      <div
        className="push-search-source-pill push-search-source-pill--pr"
        style={{ opacity: values.checkpointOpacity }}
      >
        pull request
      </div>
      <div
        className="push-search-source-pill push-search-source-pill--commit"
        style={{ opacity: values.checkpointOpacity }}
      >
        commit
      </div>
      <div
        className="push-search-source-pill push-search-source-pill--checkpoint"
        style={{ opacity: values.checkpointOpacity }}
      >
        checkpoint
      </div>

      <DiagramPanel
        accent
        className="push-search-strip"
        label="strip"
        meta={values.stripHighlight > 0.5 ? "2 of 5" : "5 blocks"}
        style={{
          opacity: values.stripOpacity,
          transform: `translateY(${(1 - values.stripOpacity) * 14}px)`,
        }}
      >
        <div className="push-search-strip__rows">
          {stripRows.map((row, index) => {
            const isKept = index === 0 || index === 4
            const rowOpacity = values.stripRowsOpacity[index] ?? 0
            const opacity = isKept ? rowOpacity : rowOpacity * (1 - values.stripHighlight * 0.68)
            return (
              <div
                key={row}
                className={`push-search-row ${isKept && values.stripHighlight > 0.05 ? "push-search-row--selected" : ""}`}
                style={{ opacity }}
              >
                {row}
              </div>
            )
          })}
        </div>
      </DiagramPanel>

      <DiagramPanel
        accent
        className="push-search-chunk"
        label="chunk"
        meta={values.chunkOpacity > 0.9 ? "1,112 tokens" : "—"}
        style={{
          opacity: values.chunkOpacity,
          transform: `translateY(${(1 - values.chunkOpacity) * 14}px)`,
        }}
      >
        {values.chunkText.map((text, index) => (
          <div className="push-search-row push-search-row--filled" key={index}>
            {text}
          </div>
        ))}
      </DiagramPanel>

      <DiagramPanel
        accent
        className="push-search-embed"
        label="embed"
        meta={values.vectorText.every(Boolean) ? "2 vectors" : "—"}
        style={{ opacity: values.embedOpacity }}
      >
        {values.vectorText.map((text, index) => (
          <div className="push-search-row push-search-row--filled" key={index}>
            {text}
          </div>
        ))}
        <div className="push-search-embed__failure" style={{ opacity: values.embedFailureOpacity }}>
          <span>nak</span>
        </div>
      </DiagramPanel>

      <div className="push-search-repo-stack" style={{ opacity: values.stackOpacity }}>
        <div className="push-search-stack-row">repo: web</div>
        <div
          className="push-search-stack-row push-search-stack-row--search"
          style={{ height: values.searchHeight }}
        >
          <div className="push-search-stack-row__title">repo: search</div>
          <div className="push-search-row">
            <span>commit</span>
            <small>in seconds</small>
          </div>
          <div
            className="push-search-row push-search-row--transcript"
            style={{ opacity: values.transcriptOpacity }}
          >
            <span>transcript</span>
            <small>~1 min</small>
          </div>
        </div>
        <div className="push-search-stack-row">repo: infra</div>
        <div className="push-search-stack-row">repo: cli</div>
      </div>

      <DiagramPanel
        className="push-search-dead-letter"
        label="dead-letter queue"
        meta="0"
        style={{ opacity: values.deadLetterOpacity }}
      >
        <span className="push-search-empty-state">nothing silently lost</span>
      </DiagramPanel>

      <DiagramPanel
        accent
        className="push-search-retry"
        label="retry lane"
        meta="backoff"
        style={{
          opacity: values.retryOpacity,
          transform: `translateY(${(1 - values.retryOpacity) * 14}px)`,
        }}
      >
        <div className="push-search-backoff">
          {["1s", "2s", "4s"].map((label, index) => (
            <span key={label} style={{ opacity: values.backoff[index] }}>
              {label}
            </span>
          ))}
        </div>
      </DiagramPanel>
    </div>
  )
}

export function PushToSearchable({ color = "orange" }: { color?: PushToSearchableColor }) {
  const rootRef = useRef<HTMLDivElement>(null)
  const animationFrameRef = useRef<number | null>(null)
  const previousFrameRef = useRef<number | null>(null)
  const [path, setPath] = useState<SearchPath>("embed")
  const [time, setTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const width = useElementWidth(rootRef)
  const isVisible = useIsVisible(rootRef)
  const prefersReducedMotion = usePrefersReducedMotion()
  const duration = getDuration(path)
  const displayTime = prefersReducedMotion ? duration : time
  const sceneValues = useMemo(
    () => getSceneValues(path, displayTime, width),
    [displayTime, path, width],
  )
  const sceneScale = Math.max(width / sceneWidth, width < 760 ? 0.44 : 0)

  useEffect(() => {
    if (!isVisible || !isPlaying || prefersReducedMotion) {
      previousFrameRef.current = null
      return
    }

    const animate = (timestamp: number) => {
      const previousFrame = previousFrameRef.current ?? timestamp
      const delta = Math.min(0.06, (timestamp - previousFrame) / 1000)
      previousFrameRef.current = timestamp
      setTime((currentTime) => {
        const nextTime = currentTime + delta * animationSpeed
        return nextTime >= duration ? 0 : nextTime
      })
      animationFrameRef.current = window.requestAnimationFrame(animate)
    }

    animationFrameRef.current = window.requestAnimationFrame(animate)
    return () => {
      if (animationFrameRef.current !== null) window.cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
      previousFrameRef.current = null
    }
  }, [duration, isPlaying, isVisible, prefersReducedMotion])

  function selectPath(nextPath: SearchPath) {
    setPath(nextPath)
    setTime(0)
    setIsPlaying(true)
  }

  function changeTime(event: ChangeEvent<HTMLInputElement>) {
    setTime(Number(event.currentTarget.value))
  }

  return (
    <figure
      ref={rootRef}
      className={`push-search-root push-search-root--${color} showcase-window-chrome not-prose`}
      aria-label="Push to searchable indexing flow"
    >
      <div className="push-search-paths" role="group" aria-label="Indexing path">
        <button type="button" aria-pressed={path === "embed"} onClick={() => selectPath("embed")}>
          embed
        </button>
        <button type="button" aria-pressed={path === "fail"} onClick={() => selectPath("fail")}>
          fail to embed
        </button>
      </div>

      <div className="push-search-stage" style={{ height: sceneHeight * sceneScale }}>
        <PipelineScene values={sceneValues} />
      </div>

      <div className="push-search-reader">
        <div className="push-search-timeline">
          <div className="push-search-timeline__segments" aria-hidden="true">
            {sceneValues.progress.map((progress, index) => (
              <span key={index}>
                <span style={{ width: `${progress * 100}%` }} />
              </span>
            ))}
          </div>
          <input
            type="range"
            min={0}
            max={duration}
            step={0.01}
            value={displayTime}
            onChange={changeTime}
            aria-label="Animation progress"
            disabled={prefersReducedMotion}
          />
          <button
            type="button"
            className="push-search-playback"
            onClick={() => setIsPlaying((playing) => !playing)}
            disabled={prefersReducedMotion}
          >
            {prefersReducedMotion ? "Reduced motion" : isPlaying ? "Pause" : "Play"}
          </button>
        </div>
        <figcaption className="push-search-caption">
          <strong>{sceneValues.caption.label}</strong>
          <span>{sceneValues.caption.text}</span>
        </figcaption>
      </div>
    </figure>
  )
}
